import * as React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { deliverySummary } from '../../../../../shared/riskregister/productReport';
import type { Payload, Row, ViewState } from '../../../../../shared/riskregister/types';
import '../../productReport.css';

export const ProductDeliveryChart: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
  state: ViewState;
}> = ({ payload, rows, state }) => {
  const summary = React.useMemo(() => deliverySummary(rows, payload), [payload, rows]);
  const scope = state.area === 'ALL' ? 'all platform areas' : state.area;

  if (!summary.months.length) {
    return (
      <p className="risk-register__empty">No work has been finished yet for {scope}, so there is nothing to chart.</p>
    );
  }

  const partialNote = summary.latestPartial ? ' This month is still running, so its bar will keep growing.' : '';
  const read = summary.previousMonth
    ? summary.change > 0
      ? `${summary.latest} piece${summary.latest === 1 ? '' : 's'} of work finished this month, ${summary.change} more than last month.${partialNote}`
      : summary.change < 0
        ? `${summary.latest} piece${summary.latest === 1 ? '' : 's'} of work finished this month, ${Math.abs(summary.change)} fewer than last month.${partialNote}`
        : `${summary.latest} piece${summary.latest === 1 ? '' : 's'} of work finished this month, the same as last month.${partialNote}`
    : `${summary.latest} piece${summary.latest === 1 ? '' : 's'} of work finished this month. There is no earlier month to compare it against yet.`;
  const description = summary.months.map((month) => `${month.month}: ${month.finished}`).join(', ');

  return (
    <>
      <p className="risk-register__product-read">{read}</p>
      <div
        className="risk-register__chart"
        role="img"
        aria-label={`Work finished each month for ${scope}. ${description}. ${summary.total} finished in total.`}
      >
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={summary.months} margin={{ top: 16, right: 24, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
              minTickGap={28}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fill: 'var(--text-secondary)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--border)' }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
            />
            <Bar dataKey="finished" name="Work finished" fill="var(--accent-green)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="risk-register__note">
        A month counts a piece of work once it is closed out as done. Work that was dropped rather than delivered is not
        counted here.
      </p>
    </>
  );
};
